/**
 * Sample text a walkthrough step can drop into a box for the user (the
 * Import from CSV walkthrough's "generate a sample" buttons). Samples live in
 * the catalog as plain text with date placeholders, so they never go stale:
 *
 *   {OPEN_DATE}     an open date about six weeks ago (MM/DD/YYYY)
 *   {EXPIRY_NEAR}   the next standard monthly option expiration (third
 *                   Friday) at least six weeks out
 *   {EXPIRY_FAR}    the monthly expiration after that
 *
 * Six weeks keeps the open date a real backdated import (history rebuilds
 * from it) while staying short enough to rebuild quickly, and keeps every
 * sample contract far from expiry on the day it is pasted.
 */

const DAY_MS = 24 * 60 * 60 * 1000;
const OPEN_DAYS_AGO = 45;
const MIN_DAYS_TO_EXPIRY = 42;

export function fillSampleDates(text: string, today: Date = new Date()): string {
  const near = nextMonthlyExpiration(new Date(today.getTime() + MIN_DAYS_TO_EXPIRY * DAY_MS));
  const far = thirdFriday(near.getFullYear(), near.getMonth() + 1);
  const open = new Date(today.getTime() - OPEN_DAYS_AGO * DAY_MS);
  return text
    .replaceAll("{OPEN_DATE}", formatUsDate(open))
    .replaceAll("{EXPIRY_NEAR}", formatUsDate(near))
    .replaceAll("{EXPIRY_FAR}", formatUsDate(far));
}

/** The third Friday of a month (local time); `monthIndex` is 0-based and may overflow into the next year. */
export function thirdFriday(year: number, monthIndex: number): Date {
  const first = new Date(year, monthIndex, 1);
  const offsetToFriday = (5 - first.getDay() + 7) % 7;
  return new Date(first.getFullYear(), first.getMonth(), 1 + offsetToFriday + 14);
}

/** The first standard monthly expiration on or after `from`. */
function nextMonthlyExpiration(from: Date): Date {
  const floor = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  for (let offset = 0; offset < 3; offset += 1) {
    const candidate = thirdFriday(from.getFullYear(), from.getMonth() + offset);
    if (candidate >= floor) return candidate;
  }
  return thirdFriday(from.getFullYear(), from.getMonth() + 3);
}

function formatUsDate(date: Date): string {
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${date.getFullYear()}`;
}
